<template>
  <div class="BuyBoxSamplesOnly">
    <!-- * * * * order samples
    Don't show samples if we are already on a sample page
    -->
    <button
      v-if="hasSampleRelations && !activeVariant.partNo?.startsWith('SA0')"
      class="btn w-full btn--lg mb-20 desk:mb-24"
      :class="{
        loading: isLoading,
        'btn--secondary': marketSample?.partNo && cartStore.getQuantityFromPartNo(marketSample?.partNo) > 0,
      }"
      :disabled="isLoading"
      @click="handleSampleClick"
    >
      <span v-if="marketSample?.partNo && cartStore.getQuantityFromPartNo(marketSample?.partNo) > 0">
        {{ $t('quickbuy.button.inCart') }}
      </span>
      <span v-else>
        {{ $t('samples.b2c.free') }}
      </span>
    </button>

    <!-- Old version
    <div v-if="marketSample?.partNo && !activeVariant.partNo?.startsWith('SA0')" class="mt-16 mb-24 bg-bg-brandSubtle p-16 rounded-2">
      <div class="type-headline-sm text-text-base mb-2">
        {{ $t('productPage.sample.title', {name: activeVariant.name }) }}
      </div>
      <div class="type-sm text-text-subtle mb-12">
        {{ $t('productPage.sample.description') }}
      </div>
      <button
        class="btn w-full btn--lg"
        :class="{
          loading,
          'btn--secondary': cartStore.getQuantityFromPartNo(marketSample?.partNo) > 0,
        }"
        @click="addSampleToCart(marketSample?.partNo)"
      >
        <span v-if="cartStore.getQuantityFromPartNo(marketSample?.partNo) > 0">
          {{ $t('quickbuy.button.inCart') }}
        </span>
        <span v-else>
          {{ $t('samples.b2c.free') }}
        </span>
      </button>
    </div> -->
  </div>
</template>

<script setup lang="ts">
import { ProductVariantModel } from '~/models/productVariant';
import { NorceProductTypes, NorceRelatedProductTypeCodes } from '~/constants/norceCodes';
import useAlgoliaFetch from '~/composeables/useAlgoliaFetch';
import Product, { ProductModel } from '~/models/product';
import { useCartStore } from '~/store/cart';
import useLinkReplacer from '~/composeables/useLinkReplacer';
import { useGlobalContentStore } from '~/store/globalContent';
import { createDataLayerItem, sendDataLayer } from '~/composeables/useTracking';
const { getProductByPartNo } = useAlgoliaFetch();

const { getExactVariantByPartNo, lastError, lastErrorCode } = useAlgoliaFetch();
const cartStore = useCartStore();
const globalContent = useGlobalContentStore();

const props = defineProps<{
  productItem: ProductModel,
  activeVariant: ProductVariantModel,
}>();

/**
 * Samples are loaded on demand when the user requests one.
 *
 * B2C should only have one sample, so we slice the array
 */
const samplesHydrated = ref<ProductModel[]>([]);
const marketSample = ref<ProductModel>();
const hasLoadedSamples = ref(false);
const loadingSamples = ref(false);
const hasSampleRelations = computed(() => {
  return props.activeVariant.relatedProducts.some(f => f.code === NorceRelatedProductTypeCodes.Samples);
});
const isLoading = computed(() => loading.value || loadingSamples.value);

const loadSamples = async() => {
  if (hasLoadedSamples.value || loadingSamples.value) {
    return;
  }
  loadingSamples.value = true;
  const samples = props.activeVariant.relatedProducts.filter(f => f.code === NorceRelatedProductTypeCodes.Samples).map(m => m.partNo);
  if (samples.length) {
    const res = await getExactVariantByPartNo(samples);
    if (res) {
      samplesHydrated.value = res.hits.map(hit => Product.create([hit]));
      if (globalContent.getIsB2c) {
        if (globalContent.domainMap?.countryCode === 'gb') {
          // For UK market, use ExtendedAssortment product
          marketSample.value = samplesHydrated.value?.find((s) => s.type === NorceProductTypes.ExtendedAssortment);
        } else if (globalContent.domainMap?.countryCode === 'us') {
          marketSample.value = samplesHydrated.value?.find((s) => s.partNo?.toUpperCase().startsWith('US01'));
        } else {
          marketSample.value = samplesHydrated.value?.find((s) => s.partNo?.toUpperCase().startsWith('SA01'));
        }
      } else {
        marketSample.value = samplesHydrated.value[0];
      }
    } else {
      console.log(lastError.value);
      console.log(lastErrorCode.value);
    }
  }
  hasLoadedSamples.value = true;
  loadingSamples.value = false;
};

const loading = ref(false);
const { manualUrl } = useLinkReplacer();

const addSampleToCart = async(partNo: string) => {
  if (cartStore.getQuantityFromPartNo(partNo) > 0) {
    navigateTo(manualUrl('/checkout'));
  } else {
    await cartStore.updateCart(partNo);

    // tracking
    const item = await getProductByPartNo(partNo);
    if (item?.hits && item.hits.length) {
      sendDataLayer(
        'add_sample_to_cart',
        [ createDataLayerItem(item.hits[0]) ]
      );
    }
  }
};

const handleSampleClick = async() => {
  if (isLoading.value) {
    return;
  }
  loading.value = true;
  await loadSamples();
  const partNo = marketSample.value?.partNo;
  if (!partNo) {
    loading.value = false;
    return;
  }
  await addSampleToCart(partNo);
  loading.value = false;
};

</script>

<script setup lang="ts">
</script>

<style scoped lang="postcss">
</style>
